import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format, differenceInYears, parse, addMinutes } from 'date-fns';
import { Plus, Edit2, Loader2, Search, User, X, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';
import { API_URL, TIME_SLOTS, INSTITUCIONES } from './constants';

const AppointmentModal = ({
  isOpen,
  onClose,
  isEditing,
  formData,
  setFormData,
  onSave,
  saving,
  calendarConnected,
  token,
  examTypeNames = [],  // Dynamic exam type names from API
  examTypesData = []   // Full exam types with prices
}) => {
  const [doctorSuggestions, setDoctorSuggestions] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const patientSearchRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/doctors/autocomplete`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDoctorSuggestions(response.data || []);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      }
    };
    if (isOpen) {
      fetchDoctors();
      // Reset patient search when modal opens
      setPatientSearch('');
      setSelectedPatient(null);
      setPatientSuggestions([]);
    }
  }, [isOpen, token]);

  // Search patients when typing
  useEffect(() => {
    const searchPatients = async () => {
      if (patientSearch.length < 2) {
        setPatientSuggestions([]);
        setShowPatientDropdown(false);
        return;
      }

      setSearchingPatients(true);
      try {
        const response = await axios.get(`${API_URL}/api/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const patients = response.data || [];
        const filtered = patients.filter(p => 
          p.nombre.toLowerCase().includes(patientSearch.toLowerCase()) ||
          p.telefono.includes(patientSearch)
        ).slice(0, 8);
        setPatientSuggestions(filtered);
        setShowPatientDropdown(filtered.length > 0);
      } catch (error) {
        console.error('Error searching patients:', error);
      } finally {
        setSearchingPatients(false);
      }
    };

    const debounceTimer = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounceTimer);
  }, [patientSearch, token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          patientSearchRef.current && !patientSearchRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate age from birth date
  useEffect(() => {
    if (formData.fecha_nacimiento) {
      const birthDate = new Date(formData.fecha_nacimiento);
      const age = differenceInYears(new Date(), birthDate);
      setFormData(prev => ({ ...prev, edad: age }));
    }
  }, [formData.fecha_nacimiento, setFormData]);

  // Calculate end time from start time and duration
  useEffect(() => {
    if (formData.hora_inicio && formData.duracion) {
      const startTime = parse(formData.hora_inicio, 'HH:mm', new Date());
      const endTime = addMinutes(startTime, formData.duracion);
      const endTimeStr = format(endTime, 'HH:mm');
      
      if (endTimeStr > '15:30') {
        toast.error('La cita no puede terminar después de las 3:30 PM');
      }
      
      setFormData(prev => ({ ...prev, hora_fin: endTimeStr }));
    }
  }, [formData.hora_inicio, formData.duracion, setFormData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientSearch(patient.nombre);
    setShowPatientDropdown(false);
    
    // Convert patient's examenes to appointment examenes format
    let examenes = [];
    if (patient.examenes && patient.examenes.length > 0) {
      // Use the last exam types from patient record as reference
      examenes = patient.examenes.map(exam => ({
        tipo_examen: exam.tipo_examen || '',
        duracion: exam.duracion || 30
      }));
    } else if (patient.tipo_examen) {
      // Fallback to old single tipo_examen field
      examenes = [{ tipo_examen: patient.tipo_examen, duracion: 30 }];
    } else {
      examenes = [{ tipo_examen: '', duracion: 30 }];
    }
    
    // Calculate total duration from all exams
    const totalDuracion = examenes.reduce((sum, e) => sum + (e.duracion || 30), 0);
    
    // Auto-fill ALL form fields with patient data
    setFormData(prev => ({
      ...prev,
      patient_name: patient.nombre,
      fecha_nacimiento: patient.fecha_nacimiento || '',
      edad: patient.edad || 0,
      telefono: patient.telefono || '',
      correo: patient.correo || '',
      nit: patient.nit || '',
      institucion: patient.institucion || '',
      medico_referente: patient.medico_referente || '',
      // IGSS fields
      igss_afiliacion: patient.igss_afiliacion || '',
      igss_codigo: patient.igss_codigo || '',
      igss_nog: patient.igss_nog || '',
      igss_spg: patient.igss_spg || '',
      igss_unidad_referente: patient.igss_unidad_referente || '',
      // Exams
      examenes: examenes,
      tipo_examen: examenes[0]?.tipo_examen || '',
      duracion: totalDuracion
    }));
    
    toast.success(`Datos de ${patient.nombre} cargados automáticamente`);
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setPatientSearch('');
    setFormData(prev => ({
      ...prev,
      patient_name: '',
      fecha_nacimiento: '',
      edad: 0,
      telefono: '',
      correo: '',
      nit: '',
      institucion: '',
      medico_referente: '',
      igss_afiliacion: '',
      igss_codigo: '',
      igss_nog: '',
      igss_spg: '',
      igss_unidad_referente: '',
      examenes: [{ tipo_examen: '', duracion: 30 }],
      tipo_examen: ''
    }));
  };

  // Handle exam changes
  const handleExamChange = (index, field, value) => {
    setFormData(prev => {
      const newExamenes = [...(prev.examenes || [{ tipo_examen: '', duracion: 30 }])];
      newExamenes[index] = { ...newExamenes[index], [field]: value };
      
      // Recalculate total duration
      const totalDuracion = newExamenes.reduce((sum, e) => sum + (parseInt(e.duracion) || 30), 0);
      
      // Update tipo_examen for backward compatibility (first exam)
      const tipoExamen = newExamenes.map(e => e.tipo_examen).filter(t => t).join(', ');
      
      return { 
        ...prev, 
        examenes: newExamenes,
        tipo_examen: tipoExamen,
        duracion: totalDuracion
      };
    });
  };

  // Add new exam
  const addExam = () => {
    setFormData(prev => {
      const newExamenes = [...(prev.examenes || []), { tipo_examen: '', duracion: 30 }];
      const totalDuracion = newExamenes.reduce((sum, e) => sum + (parseInt(e.duracion) || 30), 0);
      return { 
        ...prev, 
        examenes: newExamenes,
        duracion: totalDuracion
      };
    });
  };

  // Remove exam
  const removeExam = (index) => {
    setFormData(prev => {
      const newExamenes = prev.examenes.filter((_, i) => i !== index);
      if (newExamenes.length === 0) {
        newExamenes.push({ tipo_examen: '', duracion: 30 });
      }
      const totalDuracion = newExamenes.reduce((sum, e) => sum + (parseInt(e.duracion) || 30), 0);
      const tipoExamen = newExamenes.map(e => e.tipo_examen).filter(t => t).join(', ');
      return { 
        ...prev, 
        examenes: newExamenes,
        tipo_examen: tipoExamen,
        duracion: totalDuracion
      };
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="appointment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            {isEditing ? <Edit2 className="w-5 h-5 text-brand-main" /> : <Plus className="w-5 h-5 text-brand-main" />}
            {isEditing ? 'Editar Cita' : 'Nueva Cita'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4">
          {/* Patient Search with Autocomplete */}
          <div className="space-y-2">
            <Label htmlFor="patient_search">Nombre del Paciente *</Label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  ref={patientSearchRef}
                  id="patient_search"
                  value={selectedPatient ? formData.patient_name : patientSearch}
                  onChange={(e) => {
                    if (selectedPatient) {
                      handleInputChange('patient_name', e.target.value);
                    } else {
                      setPatientSearch(e.target.value);
                      handleInputChange('patient_name', e.target.value);
                    }
                  }}
                  onFocus={() => {
                    if (patientSuggestions.length > 0 && !selectedPatient) {
                      setShowPatientDropdown(true);
                    }
                  }}
                  placeholder={isEditing ? "Nombre completo" : "Buscar paciente existente o escribir nuevo..."}
                  className="bg-slate-50 pl-10 pr-10"
                  data-testid="appointment-patient-name"
                  autoComplete="off"
                />
                {searchingPatients && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                )}
                {selectedPatient && (
                  <button
                    type="button"
                    onClick={handleClearPatient}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Patient Suggestions Dropdown */}
              {showPatientDropdown && patientSuggestions.length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  <div className="p-2 text-xs text-slate-500 bg-slate-50 border-b">
                    Pacientes encontrados ({patientSuggestions.length})
                  </div>
                  {patientSuggestions.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => handleSelectPatient(patient)}
                      className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{patient.nombre}</p>
                        <p className="text-xs text-slate-500">
                          {patient.telefono} • {patient.institucion || 'Sin institución'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedPatient && (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                Paciente existente seleccionado
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento *</Label>
              <Input
                id="fecha_nacimiento"
                type="date"
                value={formData.fecha_nacimiento}
                onChange={(e) => handleInputChange('fecha_nacimiento', e.target.value)}
                className="bg-slate-50"
                data-testid="appointment-fecha-nacimiento"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edad">Edad (años)</Label>
              <Input
                id="edad"
                type="number"
                value={formData.edad}
                readOnly
                className="bg-slate-100 text-slate-600"
                data-testid="appointment-edad"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="telefono">Teléfono * (8 dígitos)</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                placeholder="0000-0000"
                maxLength={9}
                className="bg-slate-50"
                data-testid="appointment-telefono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="institucion">Institución *</Label>
              <Select 
                value={formData.institucion} 
                onValueChange={(value) => handleInputChange('institucion', value)}
              >
                <SelectTrigger className="bg-slate-50" data-testid="appointment-institucion">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {INSTITUCIONES.map((inst) => (
                    <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="medico_referente">Médico Referente</Label>
            <Input
              id="medico_referente"
              value={formData.medico_referente}
              onChange={(e) => handleInputChange('medico_referente', e.target.value)}
              placeholder="Nombre del médico que refiere"
              className="bg-slate-50"
              data-testid="appointment-medico-referente"
              list="agenda-doctors-list"
              autoComplete="off"
            />
            <datalist id="agenda-doctors-list">
              {doctorSuggestions.map((doctor, idx) => (
                <option key={idx} value={doctor} />
              ))}
            </datalist>
          </div>
          
          {/* Multiple Exams Section */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Exámenes Programados *</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addExam}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Agregar Examen
              </Button>
            </div>
            
            {(formData.examenes || [{ tipo_examen: '', duracion: 30 }]).map((exam, index) => (
              <div key={index} className="flex gap-2 items-end p-2 bg-slate-50 rounded-lg">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Tipo de Examen</Label>
                  <Input
                    value={exam.tipo_examen || ''}
                    onChange={(e) => handleExamChange(index, 'tipo_examen', e.target.value)}
                    placeholder="Ej: Refracción, OCT, etc."
                    className="bg-white h-9"
                    list={`exam-types-list-${index}`}
                    autoComplete="off"
                    data-testid={`appointment-exam-type-${index}`}
                  />
                  <datalist id={`exam-types-list-${index}`}>
                    {examTypeNames.map((type) => (
                      <option key={type} value={type} />
                    ))}
                  </datalist>
                </div>
                <div className="w-24 space-y-1">
                  <Label className="text-xs text-slate-500">Duración</Label>
                  <Input
                    type="number"
                    min={5}
                    step={5}
                    value={exam.duracion || 30}
                    onChange={(e) => handleExamChange(index, 'duracion', parseInt(e.target.value) || 30)}
                    className="bg-white h-9"
                    data-testid={`appointment-exam-duracion-${index}`}
                  />
                </div>
                {(formData.examenes?.length || 1) > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeExam(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-9 w-9"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            
            <div className="flex items-center justify-end gap-2 mt-2">
              <Label className="text-xs text-slate-500">Duración total:</Label>
              <Input
                type="number"
                min={5}
                step={5}
                value={formData.duracion || 30}
                onChange={(e) => handleInputChange('duracion', parseInt(e.target.value) || 30)}
                className="w-20 h-8 text-sm font-semibold bg-white"
                data-testid="appointment-duracion-total"
              />
              <span className="text-xs text-slate-500">min</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Fecha de Cita *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="bg-slate-50"
                data-testid="appointment-date"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="hora_inicio">Hora Inicio *</Label>
              <Select 
                value={formData.hora_inicio} 
                onValueChange={(value) => handleInputChange('hora_inicio', value)}
              >
                <SelectTrigger className="bg-slate-50" data-testid="appointment-hora-inicio">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hora_fin">Hora Fin</Label>
              <Input
                id="hora_fin"
                value={formData.hora_fin}
                readOnly
                className="bg-slate-100 text-slate-600"
                data-testid="appointment-hora-fin"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Observaciones adicionales..."
              className="bg-slate-50 resize-none"
              rows={2}
              data-testid="appointment-notes"
            />
          </div>
          
          {/* Google Calendar Sync Checkbox */}
          {calendarConnected && (
            <div className="flex items-center space-x-3 py-2 px-3 bg-blue-50 rounded-lg border border-blue-100">
              <Checkbox
                id="sync_google_calendar"
                checked={formData.sync_google_calendar}
                onCheckedChange={(checked) => handleInputChange('sync_google_calendar', checked)}
                data-testid="sync-google-calendar-checkbox"
              />
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <Label htmlFor="sync_google_calendar" className="text-sm text-blue-800 cursor-pointer">
                  Sincronizar con Google Calendar
                </Label>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-slate-300 text-slate-700"
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold"
              data-testid="save-appointment-button"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                isEditing ? 'Actualizar Cita' : 'Guardar Cita'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentModal;
